import { IInventoryManufacturerCreateProps } from "@/api_framework/api_modals/Inventory";
import Input from "@/app/components/ui/Inputs/Input/index";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useInventoryCountryListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryCountryListAPI";
import { useInventoryManufacturerCreateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryManufacturerCreateAPI";
import { useInventoryManufacturereDetailsAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryManufacturereDetailsAPI";
import { useInventoryManufacturereUpdateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryManufacturereUpdateAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { Switch } from "@headlessui/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";

export interface IInventoryBatchFormFields {
  name: string;
  origin: string;
  details: string;
  contact: string;
  status: boolean;
}
export interface IInventoryManufacturereInterface {
  source?: string;
  setOpen?: (s: boolean) => void;
  value?: string;
}

const ManufacturerAddComponent: React.FC<IInventoryManufacturereInterface> = ({ source, setOpen, value }) => {
  const navigate = useNavigate();
  const { forAllAcceptWithOutStartWithDotAndSpace } = useFormValidations();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { countryList, manufacturerDetails } = routeState.state as IInventoryState;
  const fetchCountryAPI = useInventoryCountryListAPI();
  const { execute: createManufacturere } = useInventoryManufacturerCreateAPI();
  const updateManufacturere = useInventoryManufacturereUpdateAPI();
  const fetchManufacturereDetails = useInventoryManufacturereDetailsAPI();
  const [params, _setparams] = useSearchParams();
  const isThisFormCatalogue = source === "CATALOGUE" ? true : false;
  const originList = useSelectMenuReducer(countryList, "name", "id");

  const formHook = useForm<IInventoryBatchFormFields>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    if (isThisFormCatalogue) {
      formHook.setValue("name", value);
    }
  }, [isThisFormCatalogue]);

  useEffect(() => {
    fetchCountryAPI();
  }, []);

  useEffect(() => {
    const manufactureId = params.get("manufacture_id");
    if (manufactureId) {
      fetchManufacturereDetails(manufactureId);
    }
  }, []);

  useEffect(() => {
    if (manufacturerDetails) {
      formHook.setValue("name", manufacturerDetails?.manufacturer_name);
      formHook.setValue("origin", manufacturerDetails?.origin_country);
      formHook.setValue("details", manufacturerDetails?.contact_person_details);
      formHook.setValue("contact", manufacturerDetails?.importer_contact_details);
      formHook.setValue("status", manufacturerDetails?.status);
    }
  }, [manufacturerDetails]);

  useEffect(() => {
    const value = formHook.watch("name");
    const el = value.split("");
    if (el[0] === " " || el[1] === " ") {
      formHook.setValue("name", ".");
    }
  }, [formHook.watch("name")]);

  const validateConditionalFormFields = (data: IInventoryBatchFormFields) => {
    let isValid = false;
    if (data?.name !== "") {
      isValid = true;
    }
    return isValid;
  };

  const onSubmit = (data: IInventoryBatchFormFields) => {
    const isFormSubmissionValid = validateConditionalFormFields(data);
    if (!isFormSubmissionValid) {
      return;
    }
    if (data && isFormSubmissionValid) {
      const constructedBody: IInventoryManufacturerCreateProps = {
        manufacturer_name: data?.name,
        origin_country: data?.origin,
        contact_person_details: data?.details ? data?.details : null,
        importer_contact_details: data?.contact ? data?.contact : null,
        status: data?.status ? true : false,
      };
      if (manufacturerDetails) {
        updateManufacturere(constructedBody, manufacturerDetails?.manufacturer_id).then(({ status }) => {
          if (status) {
            navigate(`/app/inventory/manufacture`);
          } else {
            setOpen(false);
          }
        });
      } else {
        createManufacturere(constructedBody).then(({ status }) => {
          if (status && !isThisFormCatalogue) {
            navigate(`/app/inventory/manufacture`);
          } else {
            setOpen(false);
          }
        });
      }
    }
  };

  const handelBackToTaxList = () => {
    if (!isThisFormCatalogue) {
      navigate(`/app/inventory/manufacture`);
    } else {
      setOpen(false);
    }
  };

  return (
    <div className="p-4 bg-white">
      <form className="w-full mt-2" onSubmit={formHook.handleSubmit(onSubmit)}>
        {!isThisFormCatalogue && (
          <>
            {" "}
            <h2 className="text-base font-semibold mb-2">Manufacturer Setup</h2>
            <hr />
          </>
        )}
        <h2 className="text-base font-semibold mt-2">Basic Information</h2>
        <div className="mt-4 flex flex-col gap-6 pb-24">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-[160px]">
              <p>
                Name <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-2/6 lg:w-1/4">
              <Input
                className="text-xs"
                inputClassName="h-[38px] rounded-lg"
                placeholder="Enter Name"
                register={formHook.register("name", {
                  required: true,
                  ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                })}
                fieldError={formHook.formState.errors.name}
                errorMessages={[
                  { message: "Name is required", type: "required" },
                  forAllAcceptWithOutStartWithDotAndSpace.errors,
                ]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0">
            <div className="w-full shrink-0 text-sm sm:text-base sm:w-[160px]">
              <p>
                Country of Origin <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-full sm:w-2/6 lg:w-1/4">
              <SearchableSelectMenu
                errorMessages={[{ message: "Country is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("origin", item.title);
                  }
                }}
                fieldError={formHook.formState.errors.origin}
                register={formHook.register("origin", { required: true })}
                selectItems={originList}
                placeholder="Select Country"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={originList?.filter((oc) => oc.title === formHook.watch("origin"))[0]}
                listBoxClassName="w-36"
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
          </div>
          <hr className="mt-6" />
          <div>
            <h2 className="text-base font-semibold mt-3">Contact Person</h2>
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0 mb-7">
              <div className="w-full shrink-0 text-sm sm:text-base sm:w-[160px]">
                <p>Details</p>
              </div>
              <div className="w-full sm:w-3/6 lg:w-1/4">
                <textarea
                  value={formHook.watch("details")}
                  className="text-xs w-full h-32 border-greay-500 rounded-md border border-gray-300"
                  placeholder="Add contact person details....."
                  onChange={(e) => formHook.setValue("details", e.target.value)}
                />
              </div>
            </div>
            <hr />
          </div>
          <div>
            <h2 className="text-base font-semibold mt-3">Importer Contact</h2>
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-0 mb-7">
              <div className="w-full shrink-0 text-sm sm:text-base sm:w-[160px]">
                <p>Details</p>
              </div>
              <div className="w-full sm:w-3/6 lg:w-1/4">
                <textarea
                  value={formHook.watch("contact")}
                  className="text-xs w-full h-32 border-greay-500 rounded-md border border-gray-300"
                  placeholder="Add contact person details....."
                  onChange={(e) => formHook.setValue("contact", e.target.value)}
                />
              </div>
            </div>
            <hr />
          </div>
          <div className="flex items-center justify-between gap-3 sm:justify-start sm:gap-0">
            <div className="shrink-0 text-sm sm:text-base sm:w-[160px]">
              <p>Status</p>
            </div>
            <div className="shrink-0">
              <Switch
                checked={formHook.watch("status")}
                onChange={() => {
                  if (formHook.watch("status")) {
                    formHook.setValue("status", false);
                  } else {
                    formHook.setValue("status", true);
                  }
                }}
                className={`${formHook.watch("status") ? "bg-[#4D9FFF]" : "bg-[#CCCCCC]"}
          relative inline-flex h-6 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75`}
              >
                <span
                  aria-hidden="true"
                  className={`${formHook.watch("status") ? "translate-x-8" : "translate-x-0"}
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          </div>
        </div>
        <div className="flex justify-end p-4 gap-4 items-center bg-white bottom-0 right-0 w-full border-t-2 fixed">
          <button type="button" className={SECONDARY_BUTTON} onClick={() => handelBackToTaxList()}>
            Cancel
          </button>
          <button type="submit" className={PRIMARY_BUTTON}>
            {manufacturerDetails ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};
export default ManufacturerAddComponent;
